import { useDateNow, useReduxState } from 'hooks';
import { getFormattedDate } from 'lib/common';
import { Badge, DisplayFormattedDate } from 'components/common';
import { BoxTableCell } from 'components/common/box';

import type { PageState } from 'types';

interface Props {
  returnPart: 'header' | 'body';
  user?: PageState['user'];
}

const UsersViewOverview = ({ returnPart, user }: Props) => {
  const { dateNow } = useDateNow();

  const { messages, serverConfig } = useReduxState();

  const achievementsVisible = !serverConfig.hideAchievementsFromUI;
  const coursesVisible = !serverConfig.hideCoursesFromUI;
  const pathsVisible = !serverConfig.hidePathsFromUI;

  return returnPart === 'header' ? (
    <>
      {coursesVisible && <BoxTableCell>{messages.commonCourses}</BoxTableCell>}
      <BoxTableCell>{messages.commonGroups}</BoxTableCell>
      {pathsVisible && <BoxTableCell>{messages.commonPaths}</BoxTableCell>}
      {achievementsVisible && (
        <BoxTableCell>{messages.commonAchievementsAlt}</BoxTableCell>
      )}
      <BoxTableCell>{messages.commonAccount}</BoxTableCell>
      <BoxTableCell>{messages.usersLastLogin}</BoxTableCell>
    </>
  ) : returnPart === 'body' && user ? (
    <>
      {coursesVisible && (
        <BoxTableCell label={messages.commonCourses}>
          {user.numberOfCourses}
        </BoxTableCell>
      )}
      <BoxTableCell label={messages.commonGroups}>
        {user.numberOfGroups}
      </BoxTableCell>
      {pathsVisible && (
        <BoxTableCell label={messages.commonPaths}>
          {user.numberOfPaths}
        </BoxTableCell>
      )}
      {achievementsVisible && (
        <BoxTableCell label={messages.commonAchievementsAlt}>
          {user.numberOfAchievements}
        </BoxTableCell>
      )}
      <BoxTableCell label={messages.commonAccount}>
        {user.roles?.includes('superadmin') ? (
          <div className="badge">
            <Badge size="small" text={messages.usersSuperadmin} />
          </div>
        ) : user.roles?.includes('admin') ? (
          <div className="badge">
            <Badge size="small" text={messages.usersAdmin} />
          </div>
        ) : null}

        {user.twoFactorAuthIsEnabled ? (
          <div className="badge">
            <Badge
              category="success-light"
              size="small"
              text={'2FA'}
              tooltip={`${
                messages.authTwoFactorAuthentication
              } ${messages.commonEnabled.toLowerCase()}`}
            />
          </div>
        ) : null}

        {user.locked ? (
          <div className="badge">
            <Badge
              category="error-light"
              icon="lock"
              size="small"
              text={messages.commonLocked}
              tooltip={messages.usersAccountLocked}
            />
          </div>
        ) : null}

        {user.expirationDate &&
        Date.parse(user.expirationDate as string) < dateNow ? (
          <div className="badge">
            <Badge
              category="error-light"
              icon="hourglass-start"
              size="small"
              text={messages.commonExpired}
              tooltip={`${messages.usersAccountExpired} ${getFormattedDate({
                date: user.expirationDate
              })}`}
            />
          </div>
        ) : user.expirationDate &&
          Date.parse(user.expirationDate as string) >= dateNow ? (
          <div className="badge">
            <Badge
              category="warning-light"
              icon="hourglass-start"
              text={getFormattedDate({ date: user.expirationDate })}
              size="small"
              tooltip={`${messages.usersAccountExpires} ${getFormattedDate({
                date: user.expirationDate
              })}`}
            />
          </div>
        ) : null}
      </BoxTableCell>
      <BoxTableCell emptyValue="-" label={messages.usersLastLogin}>
        {user.lastLogin ? <DisplayFormattedDate date={user.lastLogin} /> : null}
      </BoxTableCell>
    </>
  ) : null;
};

export default UsersViewOverview;
